import { Nav } from '@accedo/spatial-nav';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { Image } from '@accedo/ctv-image';
import { Button as CtvButton } from '@accedo/ctv-button';
import { nav } from '##/init';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { LiveShowForOverlay } from '##/services/tenApi/collections/types';
import { config } from '##/services/store/store';
import { PageNavigation } from '##/utils/PageNavigation';
import { formatTime } from '##/utils/DateTimeHelpers';
import { safeAssignFocus } from '##/utils';

const styles = stylesheet`
  @import '##/styles/import.scss';
 
.overlay-container {
  display: flex; 
  flex-direction: column; 
  justify-content: center;
  align-items: center; 
  position: absolute;
  width: pxToVw(1280);
  height: pxToVw(720);
  left: 0;
  top: 0;
  text-align: center;
  z-index: 10;

    .channel-logo {
      z-index: 1;
      height: pxToVh(40);
      width: auto;
      max-width: pxToVw(200);
      margin-bottom: pxToVh(25);
    }

    .button {
        border: pxToVw(1) solid $button-grey;
        height: pxToVw(40);
        width: pxToVw(244);
        border-radius: pxToVw(31);
        padding-bottom: pxToVh(2);
        font-size: pxToVw(18);
        font-weight: 600;
        -webkit-text-stroke: 0;
        margin-top: pxToVw(14);
        margin-bottom: pxToVw(2);
        font-family: $font-stack;
        background: #00000066 0% 0% no-repeat padding-box;
        z-index: 1;
        
        &:global(.current-focus) {
          border: pxToVw(1) solid $light-blue;
          background: transparent linear-gradient(90deg, #1882F8 0%, #0EA3FD 100%) 0% 0% no-repeat padding-box;
          outline: none;
          color: $pure-white;
          opacity:1;
        }
      }

  .content {
    max-width: pxToVw(643);
    margin-bottom: pxToVw(10);
    height: unset;
    z-index: 1;
    font-family: barlow;
    font-size: pxToVw(16);
    line-height: pxToVw(22);
  }

  .sub-content {
    color: #8994A4;
    max-width: pxToVw(643);
    margin-bottom: pxToVw(10);
    height: unset;
    z-index: 1;
    font-family: barlow;
    font-size: pxToVw(16);
    line-height: pxToVw(22);
  }

  .poster { 
      margin-bottom: pxToVw(25);
      z-index: 1
  }
  
}

.bottom-gradient {
  width: pxToVw(1280);
  height: pxToVw(720);
  left: 0;
  top: 0;
  background: $pure-black 0% 0% no-repeat padding-box;
  opacity: 0.9;
  position: absolute;
}

    
`;

interface LiveTVOverlayProps {
  setShowOverlay: (props: boolean) => void;
  liveShow: LiveShowForOverlay;
  onDismiss: () => void;
}

const goBackButtonId = 'go-back-button';
const watchOnSelectId = 'watch-on-select-button';

const LiveTVOverlayComponent: FunctionalComponent<LiveTVOverlayProps> = ({
  setShowOverlay,
  liveShow,
  onDismiss,
}) => {
  const time = Math.min(liveShow.startTime - Date.now() / 1000);
  const formattedTime = formatTime(time);
  const dismissOverlay = (): void => {
    setShowOverlay(false);
    onDismiss();
  };

  const onKeydown = (keyName: VirtualKeys): boolean => {
    if (keyName === KeyCodes.vKey.BACK) {
      dismissOverlay();
      return true;
    }
    if (
      (nav.currentFocusNodeId === (watchOnSelectId || goBackButtonId) &&
        keyName === KeyCodes.vKey.UP) ||
      keyName === KeyCodes.vKey.LEFT ||
      keyName === KeyCodes.vKey.RIGHT
    ) {
      return true;
    }
    return false;
  };

  const watchOnSelect = (endpoint: string, type: string): void => {
    if (endpoint && type) {
      const splitType = type === 'Show' ? 'shows/' : 'videos/';
      const baseUrl = config.$get()?.api.apiBaseUrl;
      const navId = endpoint.split(`${baseUrl}/api/v1/${splitType}`);
      const navPoint =
        type === 'Show' ? `/show/${navId}` : `/player/vod/${navId}`;
      PageNavigation.navigateTo(navPoint, endpoint);
    }
  };

  useEffect(() => {
    if (liveShow?.ctaButtons) {
      safeAssignFocus(watchOnSelectId, goBackButtonId);
    } else {
      safeAssignFocus(goBackButtonId);
    }
  }, []);

  const {
    ratings,
    rating,
    genre,
    landscapeImageUrl,
    title,
    description,
    ctaButtons,
    channelLogoUrl,
  } = liveShow || {};

  const liveRating = ratings || rating;

  useEffect(() => {
    KeyEventHelper.addKeydownListener(onKeydown, 'extra-overlay-listener');
    return () => {
      KeyEventHelper.removeKeydownListener('extra-overlay-listener');
    };
  }, []);

  return (
    <div className={styles.overlayContainer}>
      {channelLogoUrl && (
        <Image
          className={styles.channelLogo}
          url={`${channelLogoUrl}?image-profile=foxtel_livetv_channel_logo`}
        />
      )}
      <Image
        className={landscapeImageUrl ? styles.poster : ''}
        url={`${landscapeImageUrl}?image-profile=live_overlay_720`}
        width={268}
        height={151}
      />
      <p className={styles.content}>{title}</p>
      <p className={styles.content}>Starts in {formattedTime}</p>
      <p className={styles.subContent}>
        {liveRating} {liveRating && genre && ' | '} {genre}
      </p>
      <p className={styles.subContent}>{description}</p>

      <Nav.Vertical>
        {ctaButtons &&
          ctaButtons.map((elem) => {
            if (elem.type === 'Show' || elem.type === 'Video') {
              return (
                <CtvButton
                  onClick={() => watchOnSelect(elem.endpoint, elem.type)}
                  navId={watchOnSelectId}
                  className={styles.button}
                >
                  {elem.text}
                </CtvButton>
              );
            }
            return null;
          })}

        <CtvButton
          onClick={dismissOverlay}
          navId={goBackButtonId}
          className={styles.button}
        >
          Back
        </CtvButton>
      </Nav.Vertical>
      <div className={styles.bottomGradient} />
    </div>
  );
};

export const LiveTVOverlay: FunctionalComponent<LiveTVOverlayProps> = ({
  liveShow,
  setShowOverlay,
  onDismiss,
}: LiveTVOverlayProps) =>
  useMemo(
    () => (
      <LiveTVOverlayComponent
        onDismiss={onDismiss}
        liveShow={liveShow}
        setShowOverlay={setShowOverlay}
      />
    ),
    [liveShow, setShowOverlay],
  );
